/**
 * Outbox v2 — when the «операции не синхронизированы» banner shows (ATL-1445 stage 3, P3; plan §2
 * "Alerting", invariant 14: a write the backend refuses deterministically stays visible to the
 * cashier or an operator).
 *
 * Three states, deliberately different:
 *  - STOPPED (danger): a held entry, an unreadable record, or a foreign-install hold. Nothing behind
 *    the head moves until a person acts, so the banner shows at once and stays.
 *  - FAILING (danger): nothing is held, but the oldest entry has retried for over an hour while the
 *    device is online (`escalated`, review 1 S6): a 403, a 5xx that never clears, a CORS drop. The
 *    banner stops saying the operations "will be sent automatically".
 *  - WAITING (default): entries are queued and the oldest has waited at least
 *    `BANNER_PENDING_AFTER_MS`. This is the offline till: trading continues into the queue and the
 *    cashier is told the operations are safe on the device and will be sent. The delay is what
 *    keeps an ONLINE sale — queued, sent and acknowledged within a second — from flashing the
 *    banner on every checkout. A drain paused on `master-changed` is WAITING too: the flag re-read
 *    (P5) resolves it without a person.
 *
 * Pure, so the rule is tested without rendering; `components/OutboxV2Banner.js` renders it.
 */
import type { OutboxQueueStatus } from './outboxDrainer'

export const BANNER_PENDING_AFTER_MS = 60_000

export interface OutboxBanner {
  appearance: 'danger' | 'default'
  /** Records not yet delivered (the whole queue: everything behind a stop is unsynced too). */
  count: number
  textKey: 'outboxV2.unsyncedStopped' | 'outboxV2.unsyncedFailing' | 'outboxV2.unsynced'
}

export function outboxBannerFor(status: OutboxQueueStatus, now: number): OutboxBanner | null {
  if (!status.known || status.depth === 0) return null
  const stopped =
    status.held > 0 ||
    status.blocked > 0 ||
    status.foreignInstall > 0 ||
    status.stoppedBy === 'held' ||
    status.stoppedBy === 'blocked' ||
    status.stoppedBy === 'foreign-install'
  if (stopped) return { appearance: 'danger', count: status.depth, textKey: 'outboxV2.unsyncedStopped' }
  // Escalated (review 1 S6): retrying for over an hour while online. It may never clear on its own,
  // so the banner must not keep promising that it will.
  if (status.escalated) return { appearance: 'danger', count: status.depth, textKey: 'outboxV2.unsyncedFailing' }
  if (status.oldestEnqueuedAt !== null && now - status.oldestEnqueuedAt >= BANNER_PENDING_AFTER_MS) {
    return { appearance: 'default', count: status.depth, textKey: 'outboxV2.unsynced' }
  }
  return null
}
